/**
 * Home (#6559): what the viewer worked with recently — opened, uploaded,
 * attached to a conversation or cited in one — as a table with the reason
 * beside each name (Drive's "Reason suggested"), the folders those documents
 * sit in, and beneath it what the organization added recently, from the
 * search route with the chips applied (round 19, Claude Design: both, since
 * a viewer's own signals cannot carry a colleague's upload or a connector's
 * sync, which is most of the inflow). A viewer with no activity yet sees the
 * recent additions alone. Locked documents have their own destination in the
 * column; failed processing is ours to fix, not the user's to see here.
 */
import React, { useEffect, useMemo } from "react";
import { FolderIcon } from "@phosphor-icons/react";
import {
  CARD_GRID,
  CardPlaceholders,
  FOLDER_CARDS,
  HomeCard,
  RowPlaceholders,
  Section,
} from "./HomeSections";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import { formatInstant } from "@/utils/temporal";
import type {
  DocumentRow,
  LinkedEntity,
  Places,
  RecordSummary,
} from "./browser/api";
import { folderCardWithins } from "./browser/places";
import { reasonLabel } from "./browser/reasons";
import { recordIconKind, recordMetaLabel } from "./browser/records";
import EntityIcon from "@/components/FpPages/RMS/Dashboard/Components/EntityIcon";
import DocumentsTable, { TypeTagFor } from "./browser/DocumentsTable";
import {
  documentHref,
  inLink,
  initialsOf,
  isLocked,
  statusBucketOf,
  toRenderable,
} from "./browser/rows";
import { BrowserState, matchesFilters } from "./browser/state";
import {
  SearchOptions,
  useDocumentsSearch,
  useSuggestedDocuments,
} from "./browser/useDocumentsSearch";

export interface DocumentsHomeProps {
  state: BrowserState;
  searchOptions: SearchOptions;
  typeLabels: Record<string, string>;
  /** The uploader keys that are the viewer: `me`, and their `user:<id>`. */
  viewerKeys: string[];
  onOpenFolder: (path: string[]) => void;
  onOpenFile: (row: DocumentRow) => void;
  activeFileId: string | null;
  onDetails?: (row: DocumentRow) => void;
  onMove?: (row: DocumentRow) => void;
  onRowsChanged: () => void;
  compact?: boolean;
  dense?: boolean;
  /** The records this viewer works with, and the way into one. Empty (or
   *  absent) means the section stands down: a records section that appears
   *  because the organization qualifies and then sits empty is the same
   *  failure at a smaller scale (Claude Design, round 6b). */
  records?: RecordSummary[];
  onOpenRecord?: (record: LinkedEntity) => void;
  /** The places a location is named from (round 19): the folder cards and
   *  both tables say where a document is by its place. */
  places?: Places;
  /** True while the places read is still out: a location named before it
   *  lands would read as the tree's and flip a tick later, so the sections
   *  wait for it as the listing does — deliberately without their own
   *  skeletons meanwhile, since the read is one short request and the
   *  page is already marked busy. */
  awaitingPlaces?: boolean;
  /** Whether Home is still waiting on a read it needs before its sections
   *  can paint (#12073). The page holds its organization-wide counts until
   *  this goes false. */
  onBusyChange?: (busy: boolean) => void;
  /** True while the records are still being read (#12073, Hunter): Home
   *  holds every section until they are in, so no section paints ahead of
   *  the others. */
  awaitingRecentRecords?: boolean;
  /** True when a Recent records section is coming: its place is held with
   *  a row of card placeholders while busy. Held only when known, so the
   *  row never pops in or out of the skeleton. */
  recentRecordsExpected?: boolean;
}

/** Both tables list up to twelve rows (round 19, Claude Design Q3). */
const SUGGESTED_ROWS = 12;
const RECENT_ROWS = 12;

const DocumentsHome: React.FC<DocumentsHomeProps> = ({
  records = [],
  onOpenRecord,
  state,
  searchOptions,
  typeLabels,
  viewerKeys,
  onOpenFolder,
  onOpenFile,
  activeFileId,
  onDetails,
  onMove,
  onRowsChanged,
  compact = false,
  dense = false,
  places,
  awaitingPlaces = false,
  onBusyChange,
  awaitingRecentRecords = false,
  recentRecordsExpected = false,
}) => {
  const zone = useViewerTimeZone();
  const suggested = useSuggestedDocuments(SUGGESTED_ROWS);
  // The chips narrow the suggestions as they narrow the listing: the same
  // rules, read client-side over the rows the route returned.
  const suggestedRows = useMemo(() => {
    const now = new Date();
    return (suggested.data ?? []).filter((s) =>
      matchesFilters(s.document, state.filters, { viewerKeys, now, zone })
    );
  }, [suggested.data, state.filters, viewerKeys, zone]);
  const suggestedDocuments = useMemo(
    () => suggestedRows.map((s) => s.document),
    [suggestedRows]
  );
  const hasSuggestions = suggestedRows.length > 0;
  // What the organization added recently, beneath the viewer's own rows
  // (round 19, Claude Design Q4): the suggested rows are removed so a file
  // appears once on the page. With no suggestions, or a failed suggestions
  // read, this section stands alone. Read alongside the suggestions, not
  // after them (#12073): the removal is done here over both answers, so
  // nothing about it needs one read to finish before the other starts.
  // And with no total: the page shows none, and on a large organization
  // the organization-wide count was the whole cost of the request.
  const recent = useDocumentsSearch(state, {
    ...searchOptions,
    pageSize: RECENT_ROWS,
    withTotal: false,
  });
  const suggestedIds = useMemo(
    () => new Set(suggestedDocuments.map((row) => row.fileId)),
    [suggestedDocuments]
  );
  const recentDocs = useMemo(
    () => recent.rows.filter((row) => !suggestedIds.has(row.fileId)),
    [recent.rows, suggestedIds]
  );
  // The folder cards come from both lists, the viewer's own first.
  const folderSource = useMemo(
    () => [...suggestedDocuments, ...recentDocs],
    [suggestedDocuments, recentDocs]
  );
  const recentFolders = useMemo(() => {
    const seen = new Set<string>();
    const out: Array<{ path: string[]; driveId: string | null }> = [];
    for (const row of folderSource) {
      if (row.filePath.length === 0) continue;
      const key = JSON.stringify([row.driveId, row.filePath]);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ path: row.filePath, driveId: row.driveId });
      if (out.length === FOLDER_CARDS) break;
    }
    return out;
  }, [folderSource]);
  const folderWithins = useMemo(
    () => folderCardWithins(places, recentFolders),
    [places, recentFolders]
  );

  // The card's identity line (round 18c, Claude Design ruling A): the name
  // and the date, nothing else — the avatar already says "uploaded by", and
  // the words were what got cut while the name was hidden behind them.
  const identityFor = (row: DocumentRow): string => {
    const when = row.uploadedAt
      ? formatInstant(row.uploadedAt, zone, "monthDay")
      : null;
    if (statusBucketOf(row) === "processing") return "Processing";
    const mine = row.uploadedBy && viewerKeys.includes(row.uploadedBy.key);
    const who = mine ? "You" : (row.uploadedBy?.label ?? null);
    return [who, when].filter((part) => part !== null).join(" · ");
  };

  // The Added column: who and when, in the card's former identity line.
  const added = useMemo(
    () =>
      Object.fromEntries(
        recentDocs.map((row) => [row.fileId, identityFor(row)])
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recentDocs, zone, viewerKeys]
  );
  const reasons = useMemo(
    () =>
      Object.fromEntries(
        suggestedRows.map((s) => [
          s.document.fileId,
          reasonLabel(s.reason, zone),
        ])
      ),
    [suggestedRows, zone]
  );
  // Busy until both reads are in, not only the one a section shows: the
  // Recently added rows have the suggested rows removed from them, so
  // painting them before the suggestions land would list a file twice and
  // then take one away. The two reads run together, so the wait is the
  // slower of two short reads, not their sum. The placeholders hold the
  // sections' places meanwhile (#12073), and the page is told, so the
  // counts it shows beside the listing can follow rather than race it.
  // The records read counts too (Hunter): a section that paints ahead of
  // the others is what the placeholders exist to end.
  const busy =
    awaitingPlaces ||
    awaitingRecentRecords ||
    suggested.isLoading ||
    recent.isLoading;
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  const empty = !busy && !hasSuggestions && recentDocs.length === 0;

  return (
    <div className="flex flex-col pb-4 pt-2" aria-busy={busy}>
      {/* A failed recent read is the page's error only when it was the
          page: with the viewer's own rows on screen it simply stands down. */}
      {recent.isError && !busy && !hasSuggestions && (
        <div className="px-4 py-10 text-center text-[13px] text-[var(--fp-tag-red)]">
          The listing could not be loaded.
        </div>
      )}
      {empty && !recent.isError && (
        <div className="px-4 py-10 text-center text-[13px] text-[var(--fp-ink-3)]">
          No documents
        </div>
      )}
      {busy ? (
        <Section title="Recent folders">
          <CardPlaceholders />
        </Section>
      ) : (
        recentFolders.length > 0 && (
          <Section title="Recent folders">
            <div className={CARD_GRID}>
              {recentFolders.map(({ path, driveId }, index) => {
                const name = path[path.length - 1] ?? "";
                return (
                  <HomeCard
                    key={JSON.stringify([driveId, path])}
                    name={name}
                    meta={`In ${folderWithins[index]}`}
                    glyph={
                      <FolderIcon
                        size={18}
                        weight="fill"
                        aria-hidden
                        className="text-[var(--fp-ink-2)]"
                      />
                    }
                    onOpen={() => onOpenFolder(path)}
                  />
                );
              })}
            </div>
          </Section>
        )
      )}
      {/* Recent records, not "Your records" (Claude Design, round 7): the set is
          derived from what the viewer worked with, which is a fair ranking
          signal and a poor ownership claim — the derivation runs two hops from
          anything they did, so the names do not read as theirs. The name beside
          it is Recent folders, and this is the same claim about the same kind
          of thing. */}
      {onOpenRecord && (busy ? recentRecordsExpected : records.length > 0) && (
        <Section title="Recent records">
          {busy ? (
            <CardPlaceholders />
          ) : (
            <div className={CARD_GRID}>
              {records.map((record) => {
                return (
                  <HomeCard
                    key={record.publicId}
                    name={record.name}
                    // The count only when worth reading (round 19, Claude
                    // Design Q1): the kind alone for a record holding nothing.
                    meta={recordMetaLabel(record.kind, record.documentCount)}
                    glyph={
                      <EntityIcon
                        kind={recordIconKind(record.kind)}
                        size={18}
                        aria-hidden
                        className="text-[var(--fp-ink-2)]"
                      />
                    }
                    onOpen={() =>
                      onOpenRecord({
                        kind: record.kind,
                        publicId: record.publicId,
                        name: record.name,
                      })
                    }
                  />
                );
              })}
            </div>
          )}
        </Section>
      )}
      {!busy && hasSuggestions && (
        <Section title="Suggested for you">
          {/* Inset to the gutter with a plain header (round 18f, Claude
              Design): a table sharing the card with other sections shares
              the heading's left edge. */}
          <div className="px-4">
            <DocumentsTable
              embedded
              rows={suggestedDocuments}
              folders={[]}
              showLocation
              places={places}
              reasons={reasons}
              sort={{ by: "uploaded_at", order: "desc" }}
              onSortChange={() => undefined}
              onOpenFolder={onOpenFolder}
              onOpenFile={onOpenFile}
              rowActions
              typeLabels={typeLabels}
              activeFileId={activeFileId}
              onDetails={onDetails}
              onMove={onMove}
              compact={compact}
              dense={dense}
              isLoading={suggested.isLoading}
              isError={suggested.isError}
              onRowsChanged={() => {
                void suggested.refetch();
                onRowsChanged();
              }}
            />
          </div>
        </Section>
      )}
      {busy ? (
        <Section title="Recently added">
          <RowPlaceholders />
        </Section>
      ) : (
        recentDocs.length > 0 && (
          <Section title="Recently added">
            {/* Drive's Home is a list (round 18d, Hunter): the same table as
              Suggested for you — the name, who added it and when, where it
              is — and no type. Inset like it (round 18f). Beneath Suggested
              when both have rows (round 19), keeping its own heading and its
              Added column: that is where the other person's name lives. */}
            <div className="px-4">
              <DocumentsTable
                embedded
                rows={recentDocs}
                folders={[]}
                showLocation
                places={places}
                hideType
                reasons={added}
                reasonsLabel="Added"
                sort={{ by: "uploaded_at", order: "desc" }}
                onSortChange={() => undefined}
                onOpenFolder={onOpenFolder}
                onOpenFile={onOpenFile}
                rowActions
                typeLabels={typeLabels}
                activeFileId={activeFileId}
                onDetails={onDetails}
                onMove={onMove}
                compact={compact}
                dense={dense}
                isLoading={recent.isLoading}
                isError={recent.isError}
                onRowsChanged={() => {
                  void recent.refetch();
                  onRowsChanged();
                }}
              />
            </div>
          </Section>
        )
      )}
    </div>
  );
};

export default DocumentsHome;
